import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["list", "emptyState"]
  static values = {
    contentSelector: String,
    headingSelector: String
  }

  connect() {
    this.linksBySection = new Map()
    this.headings = []
    this.boundSyncActiveSection = this.syncActiveSection.bind(this)
    this.renderLinks()
    this.startScrollTracking()
  }

  disconnect() {
    this.stopScrollTracking()
  }

  handleClick(event) {
    const link = event.target.closest("a[data-section-id]")
    if (!link) return

    const sectionId = link.dataset.sectionId
    const heading = document.getElementById(sectionId)
    if (!heading) return

    event.preventDefault()
    this.setActiveSection(sectionId)

    const top = window.scrollY + heading.getBoundingClientRect().top - this.scrollOffset()
    window.scrollTo({ top, behavior: "smooth" })
    history.replaceState(null, "", `#${sectionId}`)
  }

  renderLinks() {
    if (!this.hasListTarget) return

    this.listTarget.innerHTML = ""
    this.linksBySection.clear()

    const headings = this.findHeadings()
    if (headings.length === 0) {
      this.headings = []
      this.showEmptyState(true)
      return
    }

    this.showEmptyState(false)

    headings.forEach((heading, index) => {
      const sectionId = this.ensureHeadingId(heading, index)
      const label = heading.textContent.trim()
      if (!label) return

      const item = document.createElement("li")
      item.className = "py-1.5 pl-3"

      const link = document.createElement("a")
      link.href = `#${sectionId}`
      link.dataset.sectionId = sectionId
      link.className = "no-underline transition-colors text-base-content/65 hover:text-base-content/95"
      link.textContent = label

      item.appendChild(link)
      this.listTarget.appendChild(item)
      this.linksBySection.set(sectionId, link)
    })

    this.headings = headings
    const initialId = this.sectionIdFromHash() || this.currentSectionIdByScroll() || this.headings[0]?.id
    if (initialId) this.setActiveSection(initialId)
  }

  findHeadings() {
    const scope = document.querySelector(this.contentSelectorValue || ".article-content")
    if (!scope) return []

    const selector = this.headingSelectorValue || "h3"
    return Array.from(scope.querySelectorAll(selector))
  }

  ensureHeadingId(heading, index) {
    if (heading.id) return heading.id

    const fallback = `section-${index + 1}`
    const baseId = this.slugify(heading.textContent) || fallback
    let uniqueId = baseId
    let suffix = 2

    while (document.getElementById(uniqueId)) {
      uniqueId = `${baseId}-${suffix}`
      suffix += 1
    }

    heading.id = uniqueId
    return uniqueId
  }

  slugify(value) {
    return value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
  }

  startScrollTracking() {
    if (this.headings.length === 0) return
    window.addEventListener("scroll", this.boundSyncActiveSection, { passive: true })
    window.addEventListener("resize", this.boundSyncActiveSection)
  }

  stopScrollTracking() {
    if (!this.boundSyncActiveSection) return
    window.removeEventListener("scroll", this.boundSyncActiveSection)
    window.removeEventListener("resize", this.boundSyncActiveSection)
  }

  syncActiveSection() {
    const sectionId = this.currentSectionIdByScroll()
    if (sectionId) this.setActiveSection(sectionId)
  }

  currentSectionIdByScroll() {
    if (this.headings.length === 0) return null

    const activationOffset = this.scrollOffset() + 12
    let activeHeading = this.headings[0]

    this.headings.forEach((heading) => {
      if (heading.getBoundingClientRect().top <= activationOffset) {
        activeHeading = heading
      }
    })

    return activeHeading?.id || null
  }

  scrollOffset() {
    const header = document.querySelector("header.navbar.sticky")
    const headerHeight = header ? header.getBoundingClientRect().height : 0
    return headerHeight + 16
  }

  sectionIdFromHash() {
    const hash = window.location.hash
    if (!hash || hash.length < 2) return null

    const sectionId = hash.slice(1)
    return this.linksBySection.has(sectionId) ? sectionId : null
  }

  setActiveSection(sectionId) {
    this.linksBySection.forEach((link, candidateId) => {
      const isActive = candidateId === sectionId
      link.classList.toggle("font-semibold", isActive)
      link.classList.toggle("text-primary", isActive)
      link.classList.toggle("text-base-content/65", !isActive)
      link.setAttribute("aria-current", isActive ? "true" : "false")
    })
  }

  showEmptyState(show) {
    if (!this.hasEmptyStateTarget) return
    this.emptyStateTarget.classList.toggle("hidden", !show)
  }
}
